<template>
	<main class="professionals-main">
		<div class="flex flex-col">
			<section class="main-banner">
				<div class="banner-text">
					Healthcare <br class="inline md:hidden">
					Professionals FAQs
				</div>
			</section>
			<section class="grid grid-cols-1">
				<div class="col-span-1">
					<nav>
						<ul class="page-nav">
							<li>
								<a href="#mc-rwe-design">How is MC RWE designed?</a>
							</li>
							<li>
								<a href="#patient-eligible">How do I know if my patient is eligible?</a>
							</li>
							<li>
								<a href="#hcp-participate">Which HCPs can participate?</a>
							</li>
							<li>
								<a href="#not-ready">What if I'm not quite ready to go digital?</a>
							</li>
							<li>
								<a href="#why-participate">Why should I participate?</a>
							</li>
							<li>
								<a href="#enroll-eportal">How do I enroll study participants through the e-portal?</a>
							</li>
							<li>
								<a href="#contact">Who do I contact if I have questions?</a>
							</li>
							<li>
								<a href="#ethics-review">Has the study been approved by an Ethics Review Board?</a>
							</li>
							<li>
								<a href="#more-info">Need more info?</a>
							</li>
						</ul>
					</nav>
				</div>
			</section>
			<section class="pt-10" id="mc-rwe-design">
				<column-content-icon>
					<template v-slot:header>
						How is MC RWE designed?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'file-medical']"/>
					</template>
					<template v-slot:content>
						<p>
							<strong>M</strong>edical <strong>C</strong>annabis <strong>R</strong>eal <strong>Wo</strong>rld <strong>E</strong>vidence is a prospective, non-interventional, observational study to explore benefits of medical cannabis in a naturalistic setting.
						</p>
					</template>
				</column-content-icon>
			</section>
			<section class="pb-4 md:pb-6">
				<div class="grid grid-cols-11 px-8 md:px-24">
					<div class="col-span-11 md:col-span-3">
						<colored-content-block bgColor="#292637" class="leading-tight">
							<p><strong>MC RWE</strong></p>
							<p>Adults ≥ 19 years with valid medical document</p>
							<p>N=1,000</p>
							<p>Chronic pain, epilepsy or issues with sleep, anxiety or depression</p>
						</colored-content-block>
					</div>
					<div class="col-span-11 md:col-span-1 flex justify-center items-center">
						<p class="plus-separator">+</p>
					</div>
					<div class="col-span-11 md:col-span-3">
						<colored-content-block bgColor="#269cbd" class="leading-tight">
							<p><strong>Verified medical cannabis products</strong></p>
						</colored-content-block>
					</div>
					<div class="col-span-11 md:col-span-1 flex justify-center items-center">
						<p class="plus-separator">+</p>
					</div>
					<div class="col-span-11 md:col-span-3">
						<colored-content-block bgColor="#869e33" class="leading-tight">
							<p><strong>Validated patient-reported outcomes</strong></p>
						</colored-content-block>
					</div>
				</div>
			</section>
			<section class="mb-8 md:mb-10">
				<professionals-diagram :data="externalStudies"/>
			</section>
			<section>
				<callout-block bgColor="#269cbd">
					<h2>This is the first study of its kind and will include participants from across Canada!</h2>
				</callout-block>
			</section>
			<section class="mt-8 md:mt-10 pb-8 md:pb-10 border-b border-black-blue border-solid" id="patient-eligible">
				<column-content-icon>
					<template v-slot:header>
						How do I know if my patient is eligible?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'check-square']"/>
					</template>
					<template v-slot:content>
						<div class="grid grid-cols-1 lg:px-24 xl:px-36">
							<div class="col-span-1">
								<simple-card cardBorderColor="#269cbd" cardHeaderBgColor="#269cbd" cardHeaderTextColor="#fff" cardContentBgColor="#eee">
									<template v-slot:header>
										<h3 class="font-bold">Inclusion Criteria</h3>
									</template>
									<template v-slot:content>
										<ul class="list-disc list-outside ml-4">
											<li>Adults aged <strong>19 years or older</strong></li>
											<li>Chronic pain, epilepsy, sleep, anxiety, depression</li>
											<li>Cannabis-naïve or cannabis-experienced</li>
										</ul>
									</template>
								</simple-card>
								<simple-card cardBorderColor="#269cbd" cardHeaderBgColor="#269cbd" cardHeaderTextColor="#fff" cardContentBgColor="#eee">
									<template v-slot:header>
										<h3 class="font-bold">Exclusion Criteria</h3>
									</template>
									<template v-slot:content>
										<ul class="list-disc list-outside ml-4">
											<li>Recreational cannabis users</li>
										</ul>
									</template>
								</simple-card>
							</div>
							<!-- <div class="col-span-2 md:col-span-1">
								<simple-card cardBorderColor="#269cbd" cardHeaderBgColor="#269cbd" cardHeaderTextColor="#fff" cardContentBgColor="#eee">
									<template v-slot:header>
										<h3 class="font-bold">Exclusion Criteria</h3>
									</template>
									<template v-slot:content>
										<ul class="list-disc list-outside ml-4">
											<li>
												<strong>Recreational</strong> cannabis users
											</li>
											<li>
												Patients already <strong>registered with <a :href="shoppersLinks[0].url" target="_blank" rel="noopener" class="text-cyan underline"><br />Medical Cannabis by Shoppers</a><sup class="text-cyan font-bold">&trade;</sup></strong> and purchased MC products from their e-commerce platform within the last 3 months
											</li>
										</ul>
									</template>
								</simple-card>
							</div> -->
						</div>
					</template>
				</column-content-icon>
			</section>
			<section class="mt-8 md:mt-10 pb-8 md:pb-10" id="hcp-participate">
				<column-content-icon>
					<template v-slot:header>
						Which HCPs can participate?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'user-md']"/>
					</template>
					<template v-slot:content>
						<p>Anyone who is a <strong>licensed health care practitioner (e.g., physician, nurse practitioner, etc.) and allowed to authorize medical cannabis can participate.</strong> We can walk you through the e-portal steps for authentication and registration. It's a seamless process for your patient to access medical cannabis products available on the MyMedi.ca website.</p>
					</template>
				</column-content-icon>
			</section>
			<section>
				<callout-block bgColor="#292637" textColor="#fff">
					<a :href="shoppersLinks[1].url" target="_blank" class="flex flex-row items-center" rel="noopener">
						<font-awesome-layers class="fa-2x fa-fw text-cyan">
							<font-awesome-icon :icon="['fas', 'hand-pointer']" transform="shrink-8"/>
							<font-awesome-icon :icon="['far', 'circle']"/>
						</font-awesome-layers>
						<h2 class="">
							<a :href="shoppersLinks[1].url" target="_blank" rel="noopener"><span class="text-cyan">Click here</span> to participate by accessing the e-portal</a>
						</h2>
					</a>
				</callout-block>
			</section>
			<section class="mt-8 md:mt-10 pb-8 md:pb-10 border-b border-black-blue border-solid" id="not-ready">
				<column-content-icon>
					<template v-slot:header>
						What if I'm not quite ready to go digital?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'fax']"/>
					</template>
					<template v-slot:content>
						<p>Print off the <a :href="rweLinks[0].url" target="_blank" rel="noopener" class="underline text-cyan font-bold">RWE medical document</a> and <a :href="rweLinks[2].url" target="_blank" rel="noopener" class="underline text-cyan font-bold">Informed Consent Form</a>.</p>
						<p>Fax both to MyMedi Patient Support Team at 1-844-500-4042.</p>
					</template>
				</column-content-icon>
			</section>
			<section class="mt-8 md:mt-10 pb-8 md:pb-10 border-b border-black-blue border-solid" id="why-participate">
				<column-content-icon>
					<template v-slot:header>
						Why should I participate?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'vial']"/>
					</template>
					<template v-slot:content>
						<ul class="list-outside list-disc ml-4">
							<li class="py-1">
								Help the industry create standards for medical cannabis and contribute to research that will aid strain selection
							</li>
							<li class="py-1">
								Give your patient a voice and improve their quality of life by studying the symptoms most important to them
							</li>
							<li class="py-1">
								Provide patient access to verified medical cannabis products
							</li>
							<li class="py-1">
								Receive comparative, aggregated data across multiple LPs
							</li>
						</ul>
					</template>
				</column-content-icon>
			</section>
			<section class="mt-8 md:mt-10 pb-8 md:pb-10 border-b border-black-blue border-solid" id="enroll-eportal">
				<column-content-icon>
					<template v-slot:header>
						How do I enroll study participants through the e-portal?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'globe']"/>
					</template>
					<template v-slot:content>
						<div class="space-y-4">
							<p class="font-bold">
								The e-portal will walk you through the necessary steps:
							</p>
							<div class="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-4 items-center justify-items-center space-y-4 text-center md:text-left">
								<img :src="icon1" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">
									Register the participant and complete the medical document in a single step through the e-portal
								</p>
								<img :src="icon2" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">
									Obtain informed consent
								</p>
								<img :src="icon3" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">
									Log in to track the study participants you have enrolled
								</p>
							</div>
						</div>
					</template>
				</column-content-icon>
			</section>
			<section class="mt-8 md:mt-10 pb-8 md:pb-10 border-b border-black-blue border-solid" id="contact">
				<column-content-icon>
					<template v-slot:header>
						Who do I contact if I have questions?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'phone']"/>
					</template>
					<template v-slot:content>
						<p>
							Reach out to the study coordinator, at <strong>416-340-4800 x 4251</strong> or email at <a href="mailto:mcrwe@uhn.ca" class="underline text-cyan font-bold">mcrwe@uhn.ca</a>.
						</p>
					</template>
				</column-content-icon>
			</section>
			<section class="py-4 md:py-6 border-b border-black-blue border-solid" id="ethics-review">
				<column-content-icon>
					<template v-slot:header>
						Has the study been approved by an Ethics Review Board?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'thumbs-up']"/>
					</template>
					<template v-slot:content>
						<p>
							Yes, the study has been approved by Veritas Independent Review Board.
						</p>
						<p>
							<a href="/documents/Annual Renewal_2026.pdf" target="_blank" rel="noopener" class="underline text-cyan font-bold">CLICK HERE</a> to see the approval document.
						</p>
					</template>
				</column-content-icon>
			</section>
			<section class="py-4 md:py-6" id="more-info">
				<column-content-icon>
					<template v-slot:header>
						Need more info?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'info']"/>
					</template>
					<template v-slot:content>
						<div class="grid grid-cols-2 md:col-gap-8">
							<div class="col-span-2 md:col-span-1 mt-4 md:mt-0">
								<h3 class="font-bold">Check out these study documents</h3>
								<icon-link-list :data="rweLinks" iconName="file-pdf"/>
							</div>
							<!-- <div class="col-span-2 md:col-span-1 mt-4 md:mt-0">
								<h3 class="font-bold">Links for validated scales</h3>
								<icon-link-list :data="externalStudies" iconName="external-link-alt"/>
							</div> -->
						</div>
						<!-- <div class="grid grid-cols-2 row-gap-8 md:row-gap-4 md:col-gap-8 ">
							<div class="col-span-2 pt-4">
								<h3 class="font-bold text-2xl">Videos</h3>
							</div>
							<div class="col-span-2 md:col-span-1">
								<h4 class="font-bold mb-4">e-Prescriber - First Time Login</h4>
								<video preload="metadata" controls>
									<source src="http://imc-na.com/RWE/MCRWE-e-prescribing-portal.mp4" type="video/mp4">
									To view this video, please update to or use a newer web browser.
								</video>
							</div>
						</div> -->
					</template>
				</column-content-icon>
			</section>
			<section>
				<callout-block bgColor="#292637" textColor="#fff">
					<a :href="shoppersLinks[1].url" target="_blank" class="flex flex-row items-center" rel="noopener">
						<font-awesome-layers class="fa-2x fa-fw text-cyan">
							<font-awesome-icon :icon="['fas', 'hand-pointer']" transform="shrink-8"/>
							<font-awesome-icon :icon="['far', 'circle']"/>
						</font-awesome-layers>
						<h2 class="">
							<a :href="shoppersLinks[1].url" target="_blank" rel="noopener"><span class="text-cyan">Click here</span> to participate by accessing the e-portal</a>
						</h2>
					</a>
				</callout-block>
			</section>
		</div>
	</main>
</template>

<script>
import Callout from '../components/Callout'
import ColumnContentIcon from '../components/ColumnContentIcon'
import ColoredContentBlock from '../components/ColoredContentBlock'
import SimpleCard from '../components/SimpleCard'
import ProfessionalsDiagram from '../components/ProfessionalsDiagram'
import IconLinkListVue from '../components/IconLinkList.vue'

import icon1 from '../assets/icons/professionals/professionals_enroll-participants_1.png'
import icon2 from '../assets/icons/professionals/professionals_enroll-participants_2.png'
import icon3 from '../assets/icons/professionals/professionals_enroll-participants_3.png'

export default {
	name: 'professionals-page',
	components: {
		'column-content-icon': ColumnContentIcon,
		'colored-content-block': ColoredContentBlock,
		'professionals-diagram': ProfessionalsDiagram,
		'callout-block': Callout,
		'simple-card': SimpleCard,
		'icon-link-list': IconLinkListVue
	},
	data() {
		return {
			icon1,
			icon2,
			icon3,
			shoppersLinks: [
				{
					url: 'https://cannabis.shoppersdrugmart.ca/en_CA',
					urlText: ''
				},
				{
					url: 'https://hcp-portal.mymedi.ca/login',
					urlText: ''
				}
			],
			rweLinks: [
				{
					url: '/documents/MyMedi_MCRWE_Medical-Document_EN_DEC-12-2023 (002).pdf',
					urlText: 'RWE medical document'
				},
				{
					url: '/documents/Protocol overview_clean_18Sep2024.docx',
					urlText: 'Protocol Brief one-pager'
				},
				{
					url: '/documents/ICF_MC-RWE_Digital_Clean_18Sep2024.docx',
					urlText: 'Informed Consent Form'
				}
			],
			externalStudies: [
				{
					url: 'https://www.ons.org/sites/default/files/2017-06/PROMIS_SF_v1_PainInterference_6a_1.pdf',
					urlText: 'PROMIS Pain Interference Short Form 6a'
				},
				{
					url: 'https://imc-na.com/RWE/2022/The Numeric Pain rating Scale.docx',
					urlText: 'Numerical rating scale'
				},
				{
					url: 'http://www.goodmedicine.org.uk/files/assessment,%20pittsburgh%20psqi.pdf',
					urlText: 'Pittsburgh Sleep Quality Index'
				},
				{
					url: 'https://imc-na.com/RWE/2022/Generalized Anxiety Disorder 7.docx',
					urlText: 'Generalized Anxiety Disorder-7 scale'
				},
				{
					url: 'https://www.med.umich.edu/1info/FHP/practiceguides/depress/phq-9.pdf',
					urlText: 'Patient Health Questionnaire-9 scale (depression)'
				},
				{
					url: 'http://imc-na.com/RWE/Sample-UK-English-EQ-5D-3L-Paper-Self-complete.pdf',
					urlText: 'EuroQoL-5D-3L (general health-related quality of life)'
				}
			]
		}
	}
}
</script>
<style lang="postcss" scoped>
	.professionals-main {
		background-color: #fff;
	}
	.main-banner {
		width: 100%;
		height: 125px;
		display: flex;
		align-items: center;
		background-image:
		linear-gradient(to top right, rgba(134, 158, 51, 0.5), rgba(38, 156, 189, 0.75)),
		url('../assets/images/1x/professionals-banner.jpg');
		background-position: center 15%;
		background-size: cover;
		background-repeat: no-repeat;
		.banner-text {
			width: 100%;
			margin: 0 2rem;
			padding: 0 0 0.5rem 0;
			font-size: 2rem;
			color: #fff;
			text-align: left;
			border-bottom: 3px solid #fff;
		}
	}
	.page-nav {
		width: 100%;
		padding: 1.5rem 2rem;
		background-color: theme('colors.black-blue');
		color: theme('colors.cyan');
		font-family: theme('fontFamily.arial-bold');
		li {
			padding: 0.5rem;
			a {
				display: inline-block;
				width: 100%;
				font-size: 1.125rem;
				text-align: left;
				transition: all 0.5s;
			}
			a:hover {
				color: #fff;
			}
		}
	}
	.plus-separator {
		font-size: 2.5rem;
		font-family: theme('fontFamily.arial-bold');
		color: #ccc;
	}
	@media screen and (min-width: 640px) {
		.main-banner {
			height: 175px;
			.banner-text {
				margin: 0 6rem;
				padding: 0 0 2rem 0;
			}
		}
		.page-nav {
			padding: 2rem 0;
			li {
				padding: 0.75rem 6rem;
				a {
					font-size: 1.825rem;
				}
			}
		}
	}
	@media screen and (min-width: 769px) {}
</style>
